import { createContext, useContext, useState } from 'react';
import { useAuthContext } from './AuthProvider';
import { useHouseholdData } from './HouseholdDataProvider';
import { useSettingsContext } from './SettingsProvider';
import { useCategoriesContext } from './CategoriesProvider';
import { useToast } from './ToastProvider';
import { useTransactions } from '@/features/transactions';
import { useBills } from '@/features/bills';
import { useBillRecurrence } from '@/features/bills/hooks/useBillRecurrence';
import { useCategoryMappings } from '@/features/import';
import { DEFAULT_DATA } from '@/shared/constants/config';

const ModalsContext = createContext(null);

export function ModalsProvider({ children }) {
  const { user } = useAuthContext();
  const { data, save, householdId } = useHouseholdData();
  const { settings, getDisplayName } = useSettingsContext();
  const { incomeList, allSubCats } = useCategoriesContext();
  const toast = useToast();

  const safe = data || DEFAULT_DATA;
  const txns = safe.txns || [];
  const bills = safe.bills || [];
  const resolvedUserName = user ? getDisplayName(user.email) : 'User';

  const tx = useTransactions({
    householdId,
    txns,
    incomeList,
    allSubCats,
    defaultPayment: settings.defaultPayment,
    resolvedUserName,
  });

  const billsHook = useBills({ householdId, data: safe, bills });
  const mappingsHook = useCategoryMappings(householdId);

  useBillRecurrence({
    data,
    save,
    defaultPayment: settings.defaultPayment,
    resolvedUserName,
    toast,
  });

  const [importOpen, setImportOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);

  const value = {
    tx,
    bills: billsHook,
    mappings: mappingsHook,
    importOpen, setImportOpen,
    fabOpen, setFabOpen,
  };

  return <ModalsContext.Provider value={value}>{children}</ModalsContext.Provider>;
}

export function useModals() {
  const ctx = useContext(ModalsContext);
  if (!ctx) throw new Error('useModals must be used within ModalsProvider');
  return ctx;
}
